/**
 * EnterpriseBillHistoryTab — sortable/searchable table of Enterprise bills.
 *
 * Extracted from Enterprise.jsx (iter200). All write actions and modal toggles
 * are passed in as props — the parent still owns the bill collection.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Trash2, CreditCard, FileText, Eye, Edit2, Download, Printer, XCircle,
} from 'lucide-react';
import ProductCodeBadge from '../ProductCodeBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);

const COLUMNS = [
  { label: 'Invoice No',  key: 'invoice_number' },
  { label: 'Client',      key: 'client' },
  { label: 'Items',       key: null },
  { label: 'Total',       key: 'total' },
  { label: 'Paid',        key: 'paid' },
  { label: 'Outstanding', key: 'outstanding' },
  { label: 'Payment',     key: 'payment_status' },
  { label: 'Date',        key: 'date' },
  { label: 'Actions',     key: null },
];

const downloadPdf = async (url, filename) => {
  const toastId = toast.loading('Preparing PDF…');
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'invoice.pdf';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }, 1000);
    toast.success('Downloaded', { id: toastId });
  } catch {
    toast.dismiss(toastId);
    try { window.open(url, '_blank', 'noopener'); } catch {}
    toast.error('Could not auto-download. Opening in new tab to Save As.');
  }
};

export default function EnterpriseBillHistoryTab({
  bills,
  filteredBills,
  searchBill,
  setSearchBill,
  filterPayment,
  setFilterPayment,
  billSortKey,
  billSortDir,
  toggleBillSort,
  isAdmin,
  fetchData,
  setPayBill,
  setShareBill,
  setPreviewBill,
  loadBillForEdit,
  requestDelete,
}) {
  const handleCleanup = async () => {
    if (!window.confirm('Drop sales_orders rows whose voucher has already been deleted? This unsticks ghost bills like TI/GST/26-27/064-069 that were left behind by an older delete path. The action is idempotent — safe to run any time.')) return;
    try {
      const r = await axios.post(`${API}/sales/orders/cleanup-orphaned`, {}, { withCredentials: true });
      toast.success(`Cleanup complete — dropped ${r.data.count} ghost bill(s)${r.data.count > 0 ? ': ' + r.data.dropped.join(', ') : ''}`);
      await fetchData();
    } catch (e) {
      toast.error('Cleanup failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleCancel = async (bill) => {
    const reason = window.prompt(`Cancel bill ${bill.invoice_number || ''}?\nThis reverses stock + cashflow effects but keeps the row for audit. Optional reason:`, '');
    if (reason === null) return;
    try {
      await axios.patch(`${API}/invoices/bill-log/${bill.id}/cancel`, { reason }, { withCredentials: true });
      toast.success(`Bill ${bill.invoice_number || ''} cancelled`);
      fetchData();
    } catch (e) {
      toast.error('Cancel failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={12} className="absolute left-2.5 top-2.5 text-zinc-500" />
          <input value={searchBill} onChange={e => setSearchBill(e.target.value)} placeholder="Search by client or invoice number..."
            data-testid="search-bills"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-7 pr-3 py-2 text-zinc-300 text-xs focus:outline-none focus:border-amber-500" />
        </div>
        <select value={filterPayment} onChange={e => setFilterPayment(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-2 text-zinc-400 text-xs focus:outline-none">
          {['All', 'Paid', 'Partial', 'Unpaid'].map(s => <option key={s} value={s} className="bg-zinc-900">{s}</option>)}
        </select>
        <span className="text-zinc-600 text-[10px] font-mono">{filteredBills.length}/{bills.length}</span>
        {isAdmin && (
          <button
            onClick={handleCleanup}
            data-testid="cleanup-orphans-btn"
            className="ml-auto flex items-center gap-1 text-[10px] px-2.5 py-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 rounded-lg"
            title="Drop sales_orders rows whose voucher is gone (cleans up old ghost bills)">
            <Trash2 size={11}/> Clean ghost bills
          </button>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="bill-history-table">
          <thead>
            <tr className="border-b border-zinc-800">
              {COLUMNS.map(({ label, key }) => {
                const active = key && key === billSortKey;
                return (
                  <th key={label}
                    onClick={key ? () => toggleBillSort(key) : undefined}
                    className={`text-left text-[10px] uppercase px-4 py-3 font-medium select-none ${key ? 'cursor-pointer hover:text-zinc-200' : ''} ${active ? 'text-amber-400' : 'text-zinc-500'}`}
                    data-testid={key ? `bill-history-sort-${key}` : undefined}
                    title={key ? 'Click to sort' : ''}>
                    {label}
                    {active && <span className="ml-1 text-[10px]">{billSortDir === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredBills.map(b => {
              const total = parseFloat(b.total_amount) || 0;
              const paid = parseFloat(b.paid_amount) || 0;
              const outstanding = Math.max(0, total - paid);
              const cancelled = b.status === 'cancelled';
              // iter203 — Items column resolution:
              //   1. >1 item  → "N items" pill
              //   2. ==1 item → that item's name (works for FG and RM since
              //      the row carries product_name regardless of item_type)
              //   3. legacy single-product orders that pre-date items[]
              //      use the top-level b.product_name.
              const firstItem = (b.items && b.items.length === 1) ? b.items[0] : null;
              const singleName = firstItem
                ? (firstItem.product_name || firstItem.material_name || '')
                : (b.product_name || '');
              const singleCode = firstItem
                ? (firstItem.product_code || '')
                : (b.product_code || '');
              return (
                <tr key={b.id}
                  className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${cancelled ? 'opacity-60' : ''}`}
                  data-testid={`bill-row-${b.id}`}>
                  <td className="px-4 py-3 font-mono text-amber-400 text-xs font-bold">
                    <span className={cancelled ? 'line-through decoration-red-500/70' : ''}>{b.invoice_number || '—'}</span>
                    {cancelled && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-red-900/40 text-red-300 border border-red-700/60 align-middle">CANCELLED</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-200 text-xs">{b.client}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">
                    {b.items?.length > 1 ? `${b.items.length} items` : (
                      <span className="inline-flex items-center gap-1.5">
                        <ProductCodeBadge name={singleName} code={singleCode} />
                        <span>{singleName || '—'}</span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-200 text-xs">
                    <span className={cancelled ? 'line-through text-zinc-500' : ''}>₹{fmt(total)}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-green-400 text-xs">₹{fmt(paid)}</td>
                  <td className="px-4 py-3 font-mono text-red-400 text-xs">{outstanding > 0 && !cancelled ? `₹${fmt(outstanding)}` : '—'}</td>
                  <td className="px-4 py-3">
                    {cancelled ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-red-900/30 text-red-300 border-red-700">Cancelled</span>
                    ) : (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                        b.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700'
                        : b.payment_status === 'Partial' ? 'bg-amber-900/40 text-amber-300 border-amber-700'
                        : 'bg-red-900/30 text-red-400 border-red-800'}`}>
                        {b.payment_status || 'Unpaid'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs font-mono">{b.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {outstanding > 0 && (
                        <button onClick={() => setPayBill(b)} className="p-1.5 rounded-lg bg-green-900/30 text-green-400 hover:text-green-300 border border-green-700/40" title="Record Payment"
                          data-testid={`record-payment-${b.id}`}>
                          <CreditCard size={12} />
                        </button>
                      )}
                      <button onClick={() => setShareBill(b)}
                        data-testid={`share-${b.id}`}
                        className="p-1.5 rounded-lg bg-green-900/30 text-green-300 hover:text-green-200 border border-green-700/40" title="WhatsApp / Email / Attachments">
                        <FileText size={12} />
                      </button>
                      <button onClick={() => setPreviewBill(b)}
                        data-testid={`view-bill-${b.id}`}
                        className="p-1.5 rounded-lg bg-zinc-800 text-blue-400 hover:text-blue-300" title="View / Print Preview"><Eye size={12} /></button>
                      <button onClick={() => loadBillForEdit(b)}
                        data-testid={`view-${b.id}`}
                        className="p-1.5 rounded-lg bg-zinc-800 text-zinc-300 hover:text-zinc-100" title="Edit invoice in form"><Edit2 size={12} /></button>
                      <button onClick={() => downloadPdf(`${API}/invoices/${b.id}/pdf`, `Invoice_${b.invoice_number || b.id}.pdf`)}
                        data-testid={`download-${b.id}`}
                        className="p-1.5 rounded-lg bg-zinc-800 text-amber-400 hover:text-amber-300" title="Download PDF"><Download size={12} /></button>
                      <button onClick={() => setPreviewBill(b)}
                        data-testid={`print-${b.id}`}
                        className="p-1.5 rounded-lg bg-zinc-800 text-blue-400 hover:text-blue-300" title="Print"><Printer size={12} /></button>
                      {isAdmin && b.status !== 'cancelled' && (
                        <button onClick={() => handleCancel(b)}
                          data-testid={`cancel-${b.id}`}
                          className="p-1.5 rounded-lg bg-amber-900/30 text-amber-400 hover:text-amber-300 border border-amber-700/30" title="Cancel bill (reverses stock & cashflow, keeps audit row)">
                          <XCircle size={12} />
                        </button>
                      )}
                      {b.status === 'cancelled' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-900/40 text-red-300 border border-red-700/50 uppercase tracking-wider" data-testid={`cancelled-tag-${b.id}`}>Cancelled</span>
                      )}
                      {isAdmin && (
                        <button onClick={() => requestDelete(`/invoices/bill-log/${b.id}`, b.invoice_number || b.client, `Delete bill ${b.invoice_number || ''}. The underlying voucher will be removed and (if linked) the Sales Order will be reverted to un-billed. 10 seconds to undo.`)}
                          data-testid={`delete-${b.id}`}
                          className="p-1.5 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30" title="Delete bill (voucher only — Sales Order kept)">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredBills.length === 0 && (
          <div className="text-center py-12 text-zinc-600 text-xs">No bills match your filter</div>
        )}
      </div>
    </div>
  );
}
